import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logger } from '@core/utils';
import { baseQuery } from '@core/api';

/**
 * Load authentication state from localStorage
 *
 * This function is called to get cached auth data for immediate render.
 */
export const loadAuthFromStorage = () => {
  try {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('userData');

    if (token && userData) {
      return {
        user: JSON.parse(userData),
        token,
        isAuthenticated: true,
      };
    }
  } catch (error) {
    logger.error('Error loading auth from localStorage:', error);
  }

  return {
    user: null,
    token: null,
    isAuthenticated: false,
  };
};

/**
 * Initialize authentication with hybrid pattern
 *
 * Flow:
 * 1. Load from localStorage (immediate render with cached data)
 * 2. Fetch /api/v1/users/me/ in background (get fresh data)
 * 3. Update localStorage with fresh data
 *
 * This provides instant UX while ensuring data freshness.
 */
export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { rejectWithValue, getState }) => {
    // Load from localStorage first
    const cached = loadAuthFromStorage();

    // If no token, return early (not authenticated)
    if (!cached.token) {
      return null;
    }

    // Fetch fresh user data from server using baseQuery
    const result = await baseQuery(
      '/api/v1/users/me/',
      { getState },
      {}
    );

    // Handle authentication errors
    if (result.error?.status === 401 || result.error?.status === 403) {
      // Token invalid, clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
      return rejectWithValue('Invalid token');
    }

    // Handle other errors - fallback to cached data
    if (result.error) {
      logger.warn('Failed to fetch fresh user data, using cached', result.error);
      return { user: cached.user, token: cached.token, fromCache: true };
    }

    const freshUser = result.data;

    // Always update localStorage with fresh data
    localStorage.setItem('userData', JSON.stringify(freshUser));
    logger.info('User data synced from server');

    return { user: freshUser, token: cached.token, fromCache: false };
  }
);

// Simple initial state - no module-level side effects
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isInitializing: false,
};

/**
 * Auth slice for managing authentication state
 *
 * Handles user credentials and token management with hybrid pattern.
 * Note: localStorage persistence for login/logout is handled by listener middleware in store.js
 *
 * Redux Toolkit 2025 Best Practices:
 * - Simple initial state (no side effects at module level)
 * - Pure reducers for sync actions
 * - Async thunks for side effects
 * - Explicit initialization via initializeAuth thunk
 */
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Set user credentials (on login)
     * localStorage persistence handled by middleware
     */
    setCredentials: (state, action) => {
      const { user, token } = action.payload;
      state.user = user;
      state.token = token;
      state.isAuthenticated = true;
    },
    /**
     * Clear auth state (on logout)
     * localStorage cleanup handled by middleware
     */
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // initializeAuth pending - load from cache immediately
      .addCase(initializeAuth.pending, (state) => {
        const cached = loadAuthFromStorage();
        state.user = cached.user;
        state.token = cached.token;
        state.isAuthenticated = cached.isAuthenticated;
        state.isInitializing = true;
      })
      // initializeAuth fulfilled - update with fresh data if different
      .addCase(initializeAuth.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.isAuthenticated = true;
        }
        state.isInitializing = false;
      })
      // initializeAuth rejected - clear state (invalid token)
      .addCase(initializeAuth.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.isInitializing = false;
      });
  },
});

export const { setCredentials, logout } = authSlice.actions;

export default authSlice.reducer;

// Selectors
export const selectCurrentUser = (state) => state.auth.user;
export const selectCurrentToken = (state) => state.auth.token;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectIsInitializing = (state) => state.auth.isInitializing;
